import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Card,
  CardContent,
  List,
  TextField,
  ListItem,
  ListItemText,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { getAllTriggers, getActions, createTriggerAction } from "../../api";
import { useAppUtilities } from '../../services/AppUtilities';
const TriggerActionCreate = () => {
  const {getActionsByEntityId } = useAppUtilities();

  const [activeStep, setActiveStep] = useState(0);
  const [triggers, setTriggers] = useState([]);
  const [selectedTrigger, setSelectedTrigger] = useState(null);
  const [conditionType, setConditionType] = useState("");
  const [availableExtActions, setAvailableExtActions] = useState([]);
  const [availableEntityActions, setAvailableEntityActions] = useState([]);
  const [selectedAction, setSelectedAction] = useState(null);
  const [selectedEntityAction, setSelectedEntityAction] = useState(null);
  const [threshold, setThreshold] = useState("");
  const [summaryDuration, setSummaryDuration] = useState({ days: 0, hours: 0, minutes: 0 });
  const [actionScope, setActionScope] = useState("global");
  const [actionValue, setActionValue] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [triggerActionName, setTriggerActionName] = useState("");
  const [triggerActionDescription, setTriggerActionDescription] = useState("");
  const [entityActions, setEntityActions] = useState([]);
  const [externalActions, setExternalActions] = useState([]);


  useEffect(() => {
    const fetchTriggers = async () => {
      try {
        const response = await getAllTriggers();
        setTriggers(response.data.triggers);
      } catch (err) {
        console.error("Error fetching triggers", err);
      }
    };
    fetchTriggers();
  }, []);


  useEffect(() => {

    if (selectedTrigger && selectedTrigger.entityTypeId) {
      setAvailableExtActions([{ name: "Send Email", type: "email" },
      { name: "Send SMS", type: "sms" }])
      setAvailableEntityActions(getActionsByEntityId(selectedTrigger.entityTypeId._id));
    }


  }, [selectedTrigger]);

  const handleSubmit = async () => {
    setLoading(true);
    const payload = {
      name: triggerActionName,
      description: triggerActionDescription,
      triggerId: selectedTrigger._id,
      conditionType,
      threshold: conditionType === "threshold" ? threshold : undefined,
      summaryDuration: conditionType === "summary" ? summaryDuration : undefined,
      entityActions,
      externalActions,
    };

    try {
      await createTriggerAction(payload);
      alert("Trigger action created successfully!");
    } catch (err) {
      setError("Failed to create trigger action");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };


  return (
    <Box p={3} borderRadius={2} maxWidth={800} mx="auto">
      <Typography variant="h5" mb={3}>
        Create Trigger Action
      </Typography>
      <Stepper activeStep={activeStep} alternativeLabel>
        {["Select Trigger", "Define Condition", "Set Action", "Review & Create"].map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>
      <Box mt={3}>
        {/* Step 0: Select Trigger */}
        {activeStep === 0 && (
          <Box>
            <TextField
              label="Trigger Action Name"
              fullWidth
              value={triggerActionName || ""}
              onChange={(e) => setTriggerActionName(e.target.value)}
              helperText="Example: Smart City Traffic Monitoring"
              sx={{ mb: 2 }}
            />
            <TextField
              label="Trigger Action Description"
              fullWidth
              multiline
              rows={4}
              value={triggerActionDescription || ""}
              onChange={(e) => setTriggerActionDescription(e.target.value)}
            />
            <Typography variant="subtitle1">Select a Trigger:</Typography>
            <List sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
              {triggers.map((trigger) => (
                <Card
                  key={trigger._id}
                  onClick={() => setSelectedTrigger(trigger)}
                  sx={{
                    padding: 2,
                    cursor: "pointer",
                    border:
                      selectedTrigger && selectedTrigger._id === trigger._id
                        ? "2px solid #1976d2"
                        : "1px solid #e0e0e0",
                  }}
                >
                  <CardContent>
                    <Typography variant="h6">{trigger.name || "Unnamed Trigger"}</Typography>
                    <Typography variant="caption">
                      Created: {new Date(trigger.createdAt).toLocaleDateString()}
                    </Typography>
                    {selectedTrigger && selectedTrigger._id === trigger._id && (
                      <CheckCircleIcon sx={{ color: "#1976d2", position: "absolute", top: 8, right: 8 }} />
                    )}
                  </CardContent>
                </Card>
              ))}
            </List>
          </Box>
        )}

        {/* Step 1: Define Condition */}
        {activeStep === 1 && (
          <Box>
            <Typography variant="subtitle1">Select Condition Type</Typography>
            <List sx={{ display: "flex", gap: 2 }}>
              {["summary", "threshold"].map((type) => (
                <Card
                  key={type}
                  onClick={() => setConditionType(type)}
                  sx={{
                    padding: 2,
                    cursor: "pointer",
                    border: conditionType === type ? "2px solid #1976d2" : "1px solid #e0e0e0",
                  }}
                >
                  <CardContent>
                    <Typography variant="h6">{type}</Typography>
                  </CardContent>
                </Card>
              ))}
            </List>
            {conditionType === "threshold" && (
              <TextField
                label="Threshold Value"
                fullWidth
                value={threshold}
                onChange={(e) => setThreshold(e.target.value)}
                margin="normal"
              />
            )}
            {conditionType === "summary" && (
              <Box>
                <TextField
                  label="Days"
                  type="number"
                  value={summaryDuration.days}
                  onChange={(e) => setSummaryDuration((prev) => ({ ...prev, days: e.target.value }))}
                  margin="normal"
                  fullWidth
                />
                <TextField
                  label="Hours"
                  type="number"
                  value={summaryDuration.hours}
                  onChange={(e) => setSummaryDuration((prev) => ({ ...prev, hours: e.target.value }))}
                  margin="normal"
                  fullWidth
                />
                <TextField
                  label="Minutes"
                  type="number"
                  value={summaryDuration.minutes}
                  onChange={(e) => setSummaryDuration((prev) => ({ ...prev, minutes: e.target.value }))}
                  margin="normal"
                  fullWidth
                />
              </Box>
            )}
          </Box>
        )}

        {/* Step 2: Set Action */}
        {activeStep === 2 && (
          <Box>
            <Typography variant="h6">Manage Actions</Typography>

            {/* Display Existing External Actions */}
            <Typography variant="subtitle1" mt={2}>
              External Actions
            </Typography>
            <List>
              {externalActions.map((action, index) => (
                <ListItem key={index}>
                  <ListItemText
                    primary={`${action.name} (${action.type})`}
                    secondary={action.value}
                  />
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={() => setExternalActions((prev) => prev.filter((_, i) => i !== index))}
                  >
                    Delete
                  </Button>
                </ListItem>
              ))}
            </List>

            {/* Display Existing Entity Actions */}
            <Typography variant="subtitle1" mt={2}>
              Entity Actions
            </Typography>
            <List>
              {entityActions.map((action, index) => (
                <ListItem key={index}>
                  <ListItemText primary={`Entity Action Name: ${action.name}`} />
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={() => setEntityActions((prev) => prev.filter((_, i) => i !== index))}
                  >
                    Delete
                  </Button>
                </ListItem>
              ))}
            </List>

            {/* Form to Add Actions */}
            <Typography variant="subtitle1" mt={2}>
              Add Action
            </Typography>
            <FormControl fullWidth margin="normal">
              <InputLabel>Action Type</InputLabel>
              <Select
                value={actionScope}
                onChange={(e) => setActionScope(e.target.value)}
              >
                <MenuItem value="global">Global Action</MenuItem>
                <MenuItem value="entity">Entity Action</MenuItem>
              </Select>
            </FormControl>
            {
              actionScope === "global" ? (<>
                <FormControl fullWidth margin="normal">
                  <InputLabel>Action</InputLabel>
                  <Select
                    value={selectedAction}
                    onChange={(e) => setSelectedAction(e.target.value)}
                  >
                    {availableExtActions.map((action) => (
                      <MenuItem key={action.name} value={action}>
                        {action.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  label={"Action Value"}
                  fullWidth
                  value={actionValue}
                  onChange={(e) => setActionValue(e.target.value)}
                  margin="normal"
                />
              </>

              ) : <FormControl fullWidth margin="normal">
                <InputLabel>Action</InputLabel>
                <Select
                  value={selectedEntityAction}
                  onChange={(e) => setSelectedEntityAction(e.target.value)}
                >
                  {availableEntityActions.map((action) => (
                    <MenuItem key={action.name} value={action}>
                      {action.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            }

            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                if (actionScope === "global") {
                  setExternalActions((prev) => [...prev, { name: selectedAction.name, type: selectedAction.type, value: actionValue }]);
                } else {
                  setEntityActions((prev) => [...prev,selectedEntityAction]);
                }
                setActionScope("global");
                setActionValue("");
              }}
            >
              Add Action
            </Button>
          </Box>
        )}



        {/* Step 3: Review */}
        {activeStep === 3 && (
          <Box>
            <Typography variant="h6">Review</Typography>
            <Typography>Trigger: {selectedTrigger?.name || "Not selected"}</Typography>
            <Typography>Condition Type: {conditionType}</Typography>
            {conditionType === "threshold" && <Typography>Threshold: {threshold}</Typography>}
            {conditionType === "summary" && (
              <Typography>
                Summary Duration: {summaryDuration.days} days, {summaryDuration.hours} hours,{" "}
                {summaryDuration.minutes} minutes
              </Typography>
            )}
          </Box>
        )}
      </Box>
      <Box mt={3} display="flex" justifyContent="space-between">
        <Button disabled={activeStep === 0} onClick={() => setActiveStep((prev) => prev - 1)}>
          Back
        </Button>
        {activeStep < 3 ? (
          <Button
            onClick={() => setActiveStep((prev) => prev + 1)}
            disabled={activeStep === 0 && !selectedTrigger}
          >
            Next
          </Button>
        ) : (
          <Button onClick={handleSubmit} disabled={loading}>
            {loading ? "Submitting..." : "Submit"}
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default TriggerActionCreate;
